/**
 * How the Live alert list is read: one standing condition per live threat,
 * repeats collapsed, resolutions demoted.
 *
 * The monitor already writes a row per detection. Showing each one is why a
 * quiet run still looks like twenty alerts. A watchlist hit that is still
 * true is a condition, not a history item — it holds one card whose numbers
 * tick. Everything else is the waterfall underneath.
 */
import {
  MissionRouteEvent,
  MissionRouteEventType,
  ROUTE_EVENT_LABELS,
} from '../../services/missionApi';
import { formatMeters } from './format';

export type AlertTone = 'crit' | 'warn' | 'ok';

export interface AlertOccurrence {
  id: number;
  at: string;
  detail?: string;
}

export interface AlertGroup {
  key: string;
  tone: AlertTone;
  title: string;
  entity: string;
  /** Operator-facing chips when one emitter has more than one live state. */
  states: string[];
  detail?: string;
  latestAt: string;
  firstAt: string;
  count: number;
  eventIds: number[];
  occurrences: AlertOccurrence[];
  standing: boolean;
  eventType: MissionRouteEventType;
  canRouteAround: boolean;
  /** Newest event that names an emitter we can put on the watchlist. */
  promoteEventId?: number | null;
  alreadyWatchlisted: boolean;
  latitude?: number | null;
  longitude?: number | null;
  acknowledged: boolean;
  drill: boolean;
  /** Mid-run escape hatch: this emitter can be trusted as own kit. */
  canExcludeOwn: boolean;
  excludeIdentifier?: string | null;
  excludeSignalType?: string | null;
  excludeName?: string | null;
  excludeManufacturerId?: number | null;
  /** Stable monitor identity — one report per watchlist / drone / follower. */
  threatKey?: string | null;
}

const STANDING_TYPES = new Set<MissionRouteEventType>([
  'watchlist_on_route',
  'cotraveler_confirmed',
  'cotraveler_sustained',
  'uas_intercept',
  'surveillance_ahead',
]);

const CRIT_TYPES = new Set<MissionRouteEventType>([
  'watchlist_on_route',
  'cotraveler_confirmed',
  'cotraveler_sustained',
  'surveillance_ahead',
  'uas_intercept',
  'threat_reacquired',
]);

const OK_TYPES = new Set<MissionRouteEventType>([
  'back_on_route',
  'signal_restored',
  'threat_stale',
  'waypoint_reached',
  'route_changed',
  'wifi_located',
  'ble_located',
  'cell_located',
  'field_report',
]);

const LOCATED_INSIGHT_TYPES = new Set<string>([
  'wifi_located',
  'ble_located',
  'cell_located',
]);

const LOCATED_INSIGHT_TITLES: Record<string, string> = {
  wifi_located: 'Wi-Fi located',
  ble_located: 'BLE located',
  cell_located: 'Cell located',
};

/** A TDOA / RSSI fix — a place we heard something, not a hostile. */
export function locatedInsightTitle(event: MissionRouteEvent): string | null {
  const fromType = event.event_type && LOCATED_INSIGHT_TYPES.has(event.event_type)
    ? event.event_type
    : '';
  const fromMeta = String(event.meta?.anomaly_type || '').toLowerCase();
  const key = fromType || (LOCATED_INSIGHT_TYPES.has(fromMeta) ? fromMeta : '');
  if (!key) return null;
  return LOCATED_INSIGHT_TITLES[key] || 'Signal located';
}

const ROUTE_AROUND_TYPES = new Set<MissionRouteEventType>([
  'watchlist_on_route',
  'cotraveler_confirmed',
  'cotraveler_sustained',
  'uas_intercept',
  'surveillance_ahead',
  'corridor_threat',
]);

const STATE_CHIPS: Partial<Record<MissionRouteEventType, string>> = {
  watchlist_on_route: 'watchlisted',
  cotraveler_confirmed: 'following',
  cotraveler_sustained: 'following',
  uas_intercept: 'drone',
  surveillance_ahead: 'surveillance',
  field_report: 'identified',
  threat_stale: 'stale',
  threat_reacquired: 'back',
};

const GENERIC_FACE_LABELS = new Set([
  'unknown', 'unidentified', 'person', 'face', 'none', 'n/a', 'unnamed',
]);

export function isIdentifiedFieldReport(event: MissionRouteEvent): boolean {
  if (event.event_type !== 'field_report') return false;
  if (event.meta?.identified) return true;
  const matches = event.meta?.face_matches;
  if (!Array.isArray(matches)) return false;
  return matches.some((raw) => {
    const name = String(raw || '').trim().split(' (cluster')[0].trim();
    return !!name && !GENERIC_FACE_LABELS.has(name.toLowerCase());
  });
}

const TONE_RANK: Record<AlertTone, number> = { ok: 0, warn: 1, crit: 2 };

function worstTone(types: MissionRouteEventType[]): AlertTone {
  return types.reduce<AlertTone>((worst, type) => {
    const tone = alertTone(type);
    return TONE_RANK[tone] > TONE_RANK[worst] ? tone : worst;
  }, 'ok');
}

const HIDDEN_UNLESS_HISTORY = new Set<MissionRouteEventType>([
  'waypoint_reached',
]);

/** Convoy state, not an emitter. The promote API needs a named radio. */
const NOT_WATCHLISTABLE = new Set<MissionRouteEventType>([
  'off_route',
  'back_on_route',
  'signal_lost',
  'signal_restored',
  'waypoint_reached',
  'route_changed',
  'operator_alert',
  'field_report',
  'threat_stale',
]);

export function alertWatchlistTarget(
  event: MissionRouteEvent,
): { eventId: number; already: boolean } | null {
  if (NOT_WATCHLISTABLE.has(event.event_type)) return null;
  const meta = event.meta || {};
  const identifier = String(meta.identifier || meta.external_id || '').trim();
  if (!identifier) return null;
  const already = event.event_type === 'watchlist_on_route'
    || meta.watchlist_id != null
    || !!meta.watchlist_label;
  return { eventId: event.id, already };
}

function eventPerson(event: MissionRouteEvent): string | null {
  const matches = event.meta?.face_matches;
  if (Array.isArray(matches)) {
    const named = matches
      .map((n) => String(n || '').trim().split(' (cluster')[0].trim())
      .find(Boolean);
    if (named) return named;
  }
  return null;
}

export function alertEntity(
  event: MissionRouteEvent,
  deviceLabel: (deviceId: string) => string,
): string {
  if (event.event_type === 'operator_alert') {
    return String(event.meta?.actor || event.device_id || '').trim();
  }
  if (event.event_type === 'field_report') {
    return eventPerson(event) || event.label || deviceLabel(event.device_id);
  }
  return event.label || deviceLabel(event.device_id);
}

export function alertTone(type: MissionRouteEventType): AlertTone {
  if (CRIT_TYPES.has(type)) return 'crit';
  if (OK_TYPES.has(type) || LOCATED_INSIGHT_TYPES.has(type)) return 'ok';
  return 'warn';
}

export function alertDetail(
  event: MissionRouteEvent,
  units: 'km' | 'mi' = 'mi',
): string | undefined {
  if (event.event_type === 'operator_alert') {
    if (event.acknowledged_at) {
      const who = (event.acknowledged_by || '').trim();
      return who ? `heard · ${who}` : 'heard';
    }
    return 'waiting for the vehicle';
  }
  if (event.event_type === 'signal_lost') return 'no fix';
  if (event.event_type === 'rf_multiloc') {
    const places = Number(event.meta?.places);
    return Number.isFinite(places) && places > 0 ? `${places} places` : undefined;
  }
  if (event.distance_m != null) {
    const span = formatMeters(event.distance_m, units);
    if (!span) return undefined;
    if (event.event_type === 'off_route') return `${span} from corridor`;
    if (STANDING_TYPES.has(event.event_type)) return `ahead ${span}`;
    return span;
  }
  return undefined;
}

function groupKey(event: MissionRouteEvent, entity: string): string {
  // Each operator message is its own row — collapsing two sends would
  // hide the second instruction.
  if (event.event_type === 'operator_alert') return `operator:${event.id}`;
  // Standing conditions and their clearance share the emitter, not the
  // event type — one card per hostile, not one card per detector.
  if (
    STANDING_TYPES.has(event.event_type)
    || isIdentifiedFieldReport(event)
    || event.event_type === 'threat_stale'
    || event.event_type === 'threat_reacquired'
  ) {
    if (event.threat_key) return `threat:${event.threat_key}`;
    return `entity:${entity.toLowerCase()}`;
  }
  if (event.threat_key) return `threat:${event.threat_key}`;
  return `kind:${event.event_type}:${entity}`;
}

const COTRAVELER_TYPES = new Set<string>([
  'cotraveler_suspected',
  'cotraveler_confirmed',
  'cotraveler_sustained',
]);

const LOCATED_OWN_TYPES = new Set<string>([
  'wifi_located',
  'ble_located',
  'cell_located',
]);

function metaManufacturerId(meta: Record<string, unknown>): number | null {
  const raw = meta.manufacturer_id;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function ownKitTarget(event: MissionRouteEvent): {
  identifier: string;
  signalType: string;
  name?: string | null;
  manufacturerId?: number | null;
} | null {
  const meta = event.meta || {};
  const fromKey = String(event.threat_key || '').replace(/^cotraveler:/i, '').trim();
  const isFollower = COTRAVELER_TYPES.has(event.event_type)
    || String(event.threat_key || '').toLowerCase().startsWith('cotraveler:');
  const isLocated = LOCATED_OWN_TYPES.has(event.event_type)
    || LOCATED_INSIGHT_TYPES.has(String(meta.anomaly_type || ''));
  if (!isFollower && !isLocated) return null;
  const identifier = String(
    meta.identifier
    || meta.external_id
    || meta.address
    || (isFollower && event.threat_key?.toLowerCase().startsWith('cotraveler:') ? fromKey : '')
    || (isLocated ? event.label : ''),
  ).trim();
  if (!identifier) return null;
  return {
    identifier,
    signalType: String(
      meta.signal_type
      || (event.event_type === 'ble_located' ? 'bluetooth' : 'wifi'),
    ),
    name: (typeof meta.ssid === 'string' && meta.ssid)
      || (typeof meta.name === 'string' && meta.name)
      || event.label
      || null,
    manufacturerId: metaManufacturerId(meta as Record<string, unknown>),
  };
}

function toOccurrence(event: MissionRouteEvent, units: 'km' | 'mi'): AlertOccurrence {
  return {
    id: event.id,
    at: event.detected_at,
    detail: alertDetail(event, units),
  };
}

function toGroup(
  events: MissionRouteEvent[],
  entity: string,
  standing: boolean,
  units: 'km' | 'mi',
): AlertGroup {
  const ordered = [...events].sort(
    (a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime(),
  );
  const latest = ordered[0];
  const oldest = ordered[ordered.length - 1];
  const located = ordered.find((e) => (
    e.latitude != null && e.longitude != null
    && Number.isFinite(Number(e.latitude)) && Number.isFinite(Number(e.longitude))
  )) || latest;
  const types = [...new Set(ordered.map((e) => e.event_type))];
  const states = types
    .map((type) => STATE_CHIPS[type] || (standing ? (ROUTE_EVENT_LABELS[type] || type) : ''))
    .filter((label, i, all) => !!label && all.indexOf(label) === i);
  const watchlist = ordered
    .map((event) => alertWatchlistTarget(event))
    .find((target) => target != null) || null;
  const own = ordered.map((event) => ownKitTarget(event)).find((target) => target != null) || null;
  const insight = locatedInsightTitle(latest);
  const identified = standing && ordered.some(isIdentifiedFieldReport);
  const title = latest.event_type === 'operator_alert'
    ? (latest.label || ROUTE_EVENT_LABELS.operator_alert)
    : insight
      ? insight
      : standing
        ? (entity || ROUTE_EVENT_LABELS[latest.event_type] || latest.event_type)
        : (ROUTE_EVENT_LABELS[latest.event_type] || latest.event_type);
  return {
    key: groupKey(latest, entity),
    tone: latest.event_type === 'operator_alert'
      ? (latest.severity === 'critical' ? 'crit' : 'warn')
      : insight
        ? 'ok'
        : identified
          ? 'crit'
        : standing
          ? (latest.event_type === 'threat_stale' ? 'warn' : worstTone(types))
          : alertTone(latest.event_type),
    title,
    entity: standing ? '' : entity,
    states,
    detail: alertDetail(latest, units),
    latestAt: latest.detected_at,
    firstAt: oldest.detected_at,
    count: ordered.length,
    eventIds: ordered.map((e) => e.id),
    occurrences: ordered.map((e) => toOccurrence(e, units)),
    standing,
    eventType: latest.event_type,
    canRouteAround: !insight && standing && types.some((type) => ROUTE_AROUND_TYPES.has(type)),
    promoteEventId: watchlist?.eventId ?? null,
    alreadyWatchlisted: !!watchlist?.already
      || types.includes('watchlist_on_route')
      || states.includes('watchlisted'),
    latitude: located.latitude,
    longitude: located.longitude,
    acknowledged: ordered.every((e) => !!e.acknowledged_at),
    drill: ordered.some((e) => e.run_kind === 'rehearsal'),
    canExcludeOwn: !!own,
    excludeIdentifier: own?.identifier ?? null,
    excludeSignalType: own?.signalType ?? null,
    excludeName: own?.name ?? null,
    excludeManufacturerId: own?.manufacturerId ?? null,
    threatKey: ordered.map((e) => e.threat_key).find(Boolean) || null,
  };
}

/** Unacknowledged critical standing cards — the Live tab badge. */
export function criticalAlertCount(
  events: MissionRouteEvent[],
  deviceLabel: (deviceId: string) => string,
): number {
  return buildAlertFeed(events, deviceLabel).standing.filter(
    (g) => g.tone === 'crit' && !g.acknowledged,
  ).length;
}

export function formatAlertBadge(count: number | null | undefined): string | number | null {
  if (count == null) return null;
  if (count <= 0) return count;
  return count > 9 ? '9+' : count;
}

export function buildAlertFeed(
  events: MissionRouteEvent[],
  deviceLabel: (deviceId: string) => string,
  options: { showHistory?: boolean; units?: 'km' | 'mi' } = {},
): { standing: AlertGroup[]; rows: AlertGroup[]; openCount: number } {
  const showHistory = !!options.showHistory;
  const units = options.units || 'mi';

  const usable = events.filter((event) => {
    if (HIDDEN_UNLESS_HISTORY.has(event.event_type) && !showHistory) return false;
    if (!showHistory && event.acknowledged_at && event.event_type !== 'operator_alert') return false;
    return true;
  });

  const buckets = new Map<string, MissionRouteEvent[]>();
  for (const event of usable) {
    const entity = alertEntity(event, deviceLabel);
    const key = groupKey(event, entity);
    const bucket = buckets.get(key) || [];
    bucket.push(event);
    buckets.set(key, bucket);
  }

  const standing: AlertGroup[] = [];
  const rows: AlertGroup[] = [];

  for (const bucket of buckets.values()) {
    const latest = [...bucket].sort(
      (a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime(),
    )[0];
    const entity = alertEntity(latest, deviceLabel);
    const everThreat = bucket.some((e) => (
      STANDING_TYPES.has(e.event_type)
      || e.event_type === 'threat_reacquired'
      || isIdentifiedFieldReport(e)
    ));
    const isStanding = everThreat && bucket.some((e) => !e.acknowledged_at);
    const group = toGroup(bucket, entity, isStanding, units);
    if (isStanding) standing.push(group);
    else rows.push(group);
  }

  const byLatest = (a: AlertGroup, b: AlertGroup) => (
    new Date(b.latestAt).getTime() - new Date(a.latestAt).getTime()
  );
  standing.sort(byLatest);
  rows.sort(byLatest);

  const quiet = new Set<MissionRouteEventType>([
    'threat_stale', 'back_on_route', 'signal_restored',
    'waypoint_reached', 'route_changed', 'wifi_located', 'ble_located',
    'cell_located', 'rf_multiloc',
  ]);
  const insightTitles = new Set<string>([...Object.values(LOCATED_INSIGHT_TITLES), 'Signal located']);
  const openCount = [...standing, ...rows].filter(
    (g) => !g.acknowledged && !quiet.has(g.eventType) && !insightTitles.has(g.title),
  ).length;

  return { standing, rows, openCount };
}

export interface DockBanner {
  kind: 'alert' | 'quiet';
  tone: AlertTone;
  title: string;
  detail?: string;
  at: string | null;
  queued: number;
  eventId?: number;
  eventType?: MissionRouteEventType;
  latitude?: number | null;
  longitude?: number | null;
}

/** Highest open standing alert, or a quiet last-update stamp. */
export function buildDockBanner(
  events: MissionRouteEvent[],
  deviceLabel: (deviceId: string) => string,
  units: 'km' | 'mi' = 'mi',
): DockBanner {
  const feed = buildAlertFeed(events, deviceLabel, { units });
  const open = feed.standing.filter((g) => !g.acknowledged && g.tone !== 'ok');
  const ranked = [...open].sort((a, b) => {
    const tone = TONE_RANK[b.tone] - TONE_RANK[a.tone];
    if (tone) return tone;
    return new Date(b.latestAt).getTime() - new Date(a.latestAt).getTime();
  });
  const top = ranked[0];
  if (!top) {
    const latest = [...events].sort(
      (a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime(),
    )[0];
    return {
      kind: 'quiet',
      tone: 'ok',
      title: 'No open alerts',
      detail: latest ? 'Last update' : 'Waiting on the monitor',
      at: latest?.detected_at || null,
      queued: 0,
    };
  }
  return {
    kind: 'alert',
    tone: top.tone,
    title: top.title,
    detail: top.detail,
    at: top.latestAt,
    queued: Math.max(0, feed.openCount - 1),
    eventId: top.eventIds[0],
    eventType: top.eventType,
    latitude: top.latitude,
    longitude: top.longitude,
  };
}
